import { type ErrorLike } from "@apollo/client";
import { Box } from "@mui/material";
import { useTheme } from "@mui/system";
import { type GridRenderCellParams, type GridRowClassNameParams } from "@mui/x-data-grid-pro";
import { type ColumnDefinition, Typography, useDataGridApi, useDataGridFiltering, useDataGridPaging, useDataGridSorting } from "@pmag/ui-components";
import { type GQLHotspotFragment, type GQLSubGroupDetailSparePartFragment } from "@src/graphql.generated";
import { useAttributeConfig } from "@src/hooks/useAttributeConfig";
import { AvailabilityStatusCell } from "@src/pages/subGroupDetail/subgroupDetailsListSection/availabilityStatusCell/AvailabilityCell";
import { PriceCell } from "@src/pages/subGroupDetail/subgroupDetailsListSection/priceCell/PriceCell";
import { type SPFArticleWithPriceInfo } from "@src/utils/events";
// eslint-disable-next-line import/no-duplicates
import type * as React from "react";
// eslint-disable-next-line import/no-duplicates
import { type FunctionComponent, useMemo } from "react";
import { useIntl } from "react-intl";

import { ArticleImage, ArticleInfoBox, DataGridRowStyled, LineNumberChip, SparePartsListDataGrid, StyledTypography } from "./SparePartsList.styles";

export const dataTestId = {
    container: "sparePartsFinder.subgroupPage.sparePartsList.container",
    cellLineNumber: "sparePartsFinder.subgroupPage.sparePartsList.grid.cell.lineNumber",
    cellArticleImage: "sparePartsFinder.subgroupPage.sparePartsList.grid.cell.articleImage",
    cellArticleName: "sparePartsFinder.subgroupPage.sparePartsList.grid.cell.articleName",
    cellArticleNumber: "sparePartsFinder.subgroupPage.sparePartsList.grid.cell.articleNumber",
    cellRetailPrice: "sparePartsFinder.subgroupPage.sparePartsList.grid.cell.retailPrice",
    cellRetailPriceTitle: "sparePartsFinder.subgroupPage.sparePartsList.grid.cell.retailPrice.title",
    cellQuantity: "sparePartsFinder.subgroupPage.sparePartsList.grid.cell.quantity",
};

export enum SparePartsListField {
    ArticleInfo = "articleInfo",
    LineNumber = "lineNumber",
    RetailPrice = "retailPrice",
}

export interface SparePartsListProps {
    brand: string;
    clientWishlist: string[];
    error?: ErrorLike;
    hotspotHighlight: GQLHotspotFragment | null;
    loading: boolean;
    modelId: string;
    onRowMouseEnter: (event: React.MouseEvent<HTMLDivElement, MouseEvent>, spareParts: GQLSubGroupDetailSparePartFragment[]) => void;
    onRowMouseLeave: React.MouseEventHandler<HTMLDivElement>;
    sparePartsSorted: SPFArticleWithPriceInfo[];
}

export const SparePartsList: FunctionComponent<SparePartsListProps> = ({
    modelId,
    brand,
    loading,
    error,
    sparePartsSorted,
    onRowMouseEnter,
    onRowMouseLeave,
    hotspotHighlight,
    clientWishlist,
}) => {
    const intl = useIntl();
    const theme = useTheme();
    const { hasArticleInfos, quantityInfoDisclaimer, wishlistEnabled } = useAttributeConfig();

    const getRowClassName = (params: GridRowClassNameParams<GQLSubGroupDetailSparePartFragment>): string => {
        if (hotspotHighlight && params.row.hotspots?.find((hotspot) => hotspot.id === hotspotHighlight.id)) {
            return "Mui-highlight";
        }

        return "";
    };

    const columns = useMemo<Array<ColumnDefinition<"", "">>>(() => {
        let columns: Array<ColumnDefinition<"", "">> = [
            {
                align: "left",
                field: SparePartsListField.LineNumber,
                headerName: intl.formatMessage({
                    id: "sparePartsFinder.subgroupPage.sparePartsList.headerName.lineNumber",
                    defaultMessage: "No",
                }),
                type: "string",
                width: 60,
                sortable: false,
                filterable: false,
                resizable: false,
                renderCell: ({ row: sparePart }: GridRenderCellParams<SPFArticleWithPriceInfo, SPFArticleWithPriceInfo>) => (
                    <LineNumberChip>
                        <Typography variant="heading-09" color={theme.palette.core.white} data-testid={dataTestId.cellLineNumber}>
                            {sparePart.lineNumber}
                        </Typography>
                    </LineNumberChip>
                ),
            },
            {
                align: "left",
                field: SparePartsListField.ArticleInfo,
                headerName: intl.formatMessage({
                    id: "sparePartsFinder.subgroupPage.sparePartsList.headerName.articleInfo",
                    defaultMessage: "Article / Art. No.",
                }),
                type: "string",
                minWidth: 230,
                sortable: false,
                filterable: false,
                flex: 1,
                renderCell: ({ row: sparePart }: GridRenderCellParams<SPFArticleWithPriceInfo, SPFArticleWithPriceInfo>) => (
                    <ArticleInfoBox>
                        {sparePart.article?.assetPath && <ArticleImage src={sparePart.article.assetPath} data-testid={dataTestId.cellArticleImage} />}

                        <Box>
                            <Typography variant="heading-09" data-testid={dataTestId.cellArticleName}>
                                {sparePart.article?.name ?? "-"}

                                {sparePart.quantity > 0 && (
                                    <StyledTypography
                                        component="span"
                                        variant="body-short-01"
                                        color={theme.palette.core.grey._60}
                                        data-testid={dataTestId.cellQuantity}
                                    >
                                        {`(${sparePart.quantity})`}

                                        {quantityInfoDisclaimer && <sup>**</sup>}
                                    </StyledTypography>
                                )}
                            </Typography>

                            <Typography variant="helper-text-01" data-testid={dataTestId.cellArticleNumber}>
                                {sparePart.article?.articleNumber}
                            </Typography>
                        </Box>
                    </ArticleInfoBox>
                ),
            },
        ];
        if (hasArticleInfos) {
            columns = columns.concat([
                {
                    align: "left",
                    field: "availabilityStatus",
                    headerName: intl.formatMessage({
                        id: "sparePartsFinder.subgroupPage.sparePartsList.headerName.stockStatus",
                        defaultMessage: "Stock status",
                    }),
                    type: "string",
                    minWidth: 165,
                    sortable: false,
                    filterable: false,
                    resizable: false,
                    renderCell: ({ row: sparePart }: GridRenderCellParams<SPFArticleWithPriceInfo, SPFArticleWithPriceInfo>) => (
                        <AvailabilityStatusCell sparePart={sparePart} />
                    ),
                },
                {
                    align: "left",
                    field: SparePartsListField.RetailPrice,
                    headerName: intl.formatMessage({
                        id: "sparePartsFinder.subgroupPage.sparePartsList.headerName.price",
                        defaultMessage: "Price",
                    }),
                    type: "string",
                    minWidth: wishlistEnabled ? 450 : 400,
                    sortable: false,
                    filterable: false,
                    renderCell: ({ row: sparePart }: GridRenderCellParams<SPFArticleWithPriceInfo, SPFArticleWithPriceInfo>) => (
                        <PriceCell modelId={modelId} item={sparePart} brand={brand} clientWishlist={clientWishlist} />
                    ),
                },
            ]);
        }

        return columns;
    }, [
        intl,
        hasArticleInfos,
        theme.palette.core.white,
        theme.palette.core.grey._60,
        quantityInfoDisclaimer,
        wishlistEnabled,
        modelId,
        brand,
        clientWishlist,
    ]);

    const filtering = useDataGridFiltering({ columns, storage: "state" });
    const sorting = useDataGridSorting({ columns, storage: "state" });
    const paging = useDataGridPaging({ storage: "state" });

    const dataGrid = useDataGridApi({
        columns,
        filtering,
        paging,
        sorting,
    });

    return (
        <Box data-testid={dataTestId.container}>
            <SparePartsListDataGrid
                {...dataGrid}
                checkboxSelection={false}
                autoHeight
                slots={{
                    ...dataGrid.slots,
                    toolbar: null,
                    row: DataGridRowStyled,
                }}
                sortingMode="client"
                filterMode="client"
                paginationModel={{ page: 0, pageSize: 100 }}
                hideFooter={true}
                loading={loading}
                error={error}
                getRowClassName={getRowClassName}
                slotProps={{
                    ...dataGrid.slotProps,
                    row: {
                        onMouseEnter: (event: React.MouseEvent<HTMLDivElement>) => onRowMouseEnter(event, sparePartsSorted),
                        onMouseLeave: onRowMouseLeave,
                    },
                }}
                rows={sparePartsSorted}
            />
        </Box>
    );
};
